import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import * as XLSX from "xlsx";

import { SubHeader } from "../../../utils/componentes-utiles/SubHeader";
import {
	createSincronizacionEoer,
	deleteSincronizacionEoer,
	getSincronizacionesEoer,
	sincronizar,
	toggleSincronizacionEoer,
	updateSincronizacionEoer,
} from "../../../services/apis";
import { formatDateTimeEs } from "../../../utils/formatters";

const weekdayOptions = [
	{ value: 1, label: "Lunes" },
	{ value: 2, label: "Martes" },
	{ value: 3, label: "Miércoles" },
	{ value: 4, label: "Jueves" },
	{ value: 5, label: "Viernes" },
	{ value: 6, label: "Sábado" },
	{ value: 7, label: "Domingo" },
];

const weekdayLabelMap = weekdayOptions.reduce((acc, option) => {
	acc[option.value] = option.label;
	return acc;
}, {});

const emptyForm = {
	id: null,
	nombre: "",
	descripcion: "",
	activo: 1,
	frecuencia_tipo: "diaria",
	dias_semana: [],
	hora_ejecucion: "04:30",
	modo_sync: "hoy",
	cantidad_dias: 1,
	nif_cabo: "",
};

const normalizeWeekdays = (days) =>
	(Array.isArray(days) ? days : [])
		.map((day) => Number(day))
		.filter((day) => day >= 1 && day <= 7)
		.filter((day, index, array) => array.indexOf(day) === index)
		.sort((a, b) => a - b);

const formatHora = (value) => {
	if (!value) return "--:--";
	return String(value).slice(0, 5);
};

const formatDiasSemana = (days) => {
	const normalized = normalizeWeekdays(days);
	if (normalized.length === 0) return "Sin días";
	return normalized.map((day) => weekdayLabelMap[day] || day).join(", ");
};

const formatProgramacion = (config) => {
	const hora = formatHora(config?.hora_ejecucion);

	if (config?.frecuencia_tipo === "semanal") {
		return `${formatDiasSemana(config?.dias_semana)} a las ${hora}`;
	}

	return `Todos los días a las ${hora}`;
};

const formatModo = (config) => {
	const modo = String(config?.modo_sync ?? "hoy");
	const dias = Number(config?.cantidad_dias ?? 1);

	if (modo === "ultimos_dias") {
		return `Últimos ${dias} día(s)`;
	}

	return "Solo hoy";
};

const formatEstadoColor = (estado) => {
	if (estado === "completada") return "success";
	if (estado === "parcial") return "warning";
	if (estado === "ejecutando") return "info";
	return "error";
};

const formatEstadoLabel = (estado) => {
	if (estado === "completada") return "Completada";
	if (estado === "parcial") return "Parcial";
	if (estado === "ejecutando") return "Ejecutando";
	return "Error";
};

const formatOrigenLabel = (origen) => (origen === "automatica" ? "Automática" : "Manual");

const toYmd = (date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const buildSyncPayloadFromConfig = (config) => {
	const today = new Date();
	const payload = {};
	const mode = String(config?.modo_sync ?? "hoy");
	const days = Math.max(1, Number(config?.cantidad_dias ?? 1));

	if (mode === "ultimos_dias") {
		const from = new Date(today);
		from.setDate(today.getDate() - (days - 1));
		payload.desde = toYmd(from);
		payload.hasta = toYmd(today);
	} else {
		payload.fecha = toYmd(today);
	}

	if (config?.nif_cabo) {
		payload.nifCabo = String(config.nif_cabo).trim();
	}

	return payload;
};

const normalizeImportRow = (row) => {
	if (!row || typeof row !== "object") {
		return row;
	}

	return Object.keys(row).reduce((acc, key) => {
		const normalizedKey = String(key).trim();
		acc[normalizedKey] = row[key] === undefined ? null : row[key];
		return acc;
	}, {});
};

const normalizeExcelDate = (value) => {
	if (!value) return null;

	if (value instanceof Date && !Number.isNaN(value.getTime())) {
		return toYmd(value);
	}

	if (typeof value === "number") {
		const parsed = XLSX.SSF.parse_date_code(value);
		if (parsed) {
			const month = String(parsed.m).padStart(2, "0");
			const day = String(parsed.d).padStart(2, "0");
			return `${parsed.y}-${month}-${day}`;
		}
	}

	const text = String(value).trim();

	if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
		return text;
	}

	if (/^\d{2}\/\d{2}\/\d{4}$/.test(text)) {
		const [day, month, year] = text.split("/");
		return `${year}-${month}-${day}`;
	}

	return text;
};

const parseImportFileRows = async (file) => {
	const arrayBuffer = await file.arrayBuffer();

	const workbook = XLSX.read(arrayBuffer, {
		type: "array",
		cellDates: true,
	});

	const sheetName = workbook.SheetNames[0];
	if (!sheetName) {
		return [];
	}

	const worksheet = workbook.Sheets[sheetName];
	if (!worksheet) {
		return [];
	}

	const rawRows = XLSX.utils.sheet_to_json(worksheet, {
		defval: null,
		raw: false,
		dateNF: "yyyy-mm-dd",
	});

	return rawRows
		.map(normalizeImportRow)
		.filter((row) =>
			Object.values(row).some((value) => value !== null && String(value).trim() !== "")
		)
		.map((row) => ({
			...row,
			FECHA: normalizeExcelDate(row.FECHA),
		}));
};

const prettyJson = (value) => {
	if (!value) return "";

	if (typeof value === "string") {
		try {
			return JSON.stringify(JSON.parse(value), null, 2);
		} catch (error) {
			return value;
		}
	}

	try {
		return JSON.stringify(value, null, 2);
	} catch (error) {
		return String(value);
	}
};

export const SincronizacionesEoer = () => {
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [runningId, setRunningId] = useState(null);

	const [schemaReady, setSchemaReady] = useState(true);
	const [missingTables, setMissingTables] = useState([]);

	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");

	const [resumen, setResumen] = useState({
		total_configuraciones: 0,
		configuraciones_activas: 0,
		ultima_ejecucion: null,
		manuales_30d: 0,
		automaticas_30d: 0,
		errores_30d: 0,
	});
	const [configuraciones, setConfiguraciones] = useState([]);
	const [historial, setHistorial] = useState([]);

	const [historialOrigen, setHistorialOrigen] = useState("todos");
	const [historialEstado, setHistorialEstado] = useState("todos");

	const [formOpen, setFormOpen] = useState(false);
	const [form, setForm] = useState(emptyForm);
	const [formError, setFormError] = useState("");

	const [detailItem, setDetailItem] = useState(null);

	const [selectedImportFile, setSelectedImportFile] = useState(null);
	const [parsedImportRows, setParsedImportRows] = useState([]);
	const [importParsing, setImportParsing] = useState(false);
	const [importingFile, setImportingFile] = useState(false);
	const [importError, setImportError] = useState("");
	const [importSuccess, setImportSuccess] = useState("");
	const [importMode, setImportMode] = useState("fecha_excel");
	const [importFecha, setImportFecha] = useState(toYmd(new Date()));
	const [importCantidadDias, setImportCantidadDias] = useState(1);
	const fileInputRef = useRef(null);

	const estadisticas = useMemo(
		() => [
			{
				label: "Configuraciones",
				value: resumen.total_configuraciones,
				helper: `${resumen.configuraciones_activas} activas`,
			},
			{
				label: "Última ejecución",
				value: resumen.ultima_ejecucion
					? formatDateTimeEs(resumen.ultima_ejecucion)
					: "Sin datos",
				helper: "Última registrada",
			},
			{
				label: "Manuales 30d",
				value: resumen.manuales_30d,
				helper: `${resumen.automaticas_30d} automáticas`,
			},
			{
				label: "Errores 30d",
				value: resumen.errores_30d,
				helper: "Historial reciente",
			},
		],
		[resumen]
	);

	const cargarDatos = useCallback(async () => {
		setLoading(true);
		setError("");

		try {
			const response = await getSincronizacionesEoer({
				limit: 40,
				origen: historialOrigen,
				estado: historialEstado,
			});

			if (response?.success) {
				setSchemaReady(Boolean(response?.schema_ready));
				setMissingTables(Array.isArray(response?.missing_tables) ? response.missing_tables : []);
				setResumen(response?.data?.resumen || {});
				setConfiguraciones(Array.isArray(response?.data?.configuraciones) ? response.data.configuraciones : []);
				setHistorial(Array.isArray(response?.data?.historial) ? response.data.historial : []);
			} else {
				setError(response?.message || "No se pudo cargar el panel de sincronizaciones");
			}
		} catch (err) {
			console.error("Error cargando sincronizaciones EOER:", err);
			setError("No se pudo cargar el panel de sincronizaciones");
		} finally {
			setLoading(false);
		}
	}, [historialEstado, historialOrigen]);

	useEffect(() => {
		cargarDatos();
	}, [cargarDatos]);

	const handleImportFileSelection = async (fileList) => {
		const file = Array.from(fileList || []).find((candidate) => candidate instanceof File) || null;

		if (!file) {
			return;
		}

		const fileName = String(file?.name || "").toLowerCase();
		if (!fileName.endsWith(".xlsx") && !fileName.endsWith(".xls") && !fileName.endsWith(".csv")) {
			setImportError("Selecciona un archivo .xlsx, .xls o .csv válido.");
			setSelectedImportFile(null);
			setParsedImportRows([]);
			return;
		}

		setImportParsing(true);
		setImportError("");
		setImportSuccess("");

		try {
			const rows = await parseImportFileRows(file);
			setSelectedImportFile(file);
			setParsedImportRows(rows);

			if (rows.length === 0) {
				setImportError("El archivo no contiene filas reconocibles.");
			} else {
				setImportSuccess(`Archivo parseado correctamente: ${rows.length} fila(s).`);
			}
		} catch (err) {
			console.error("Error parseando archivo de importación:", err);
			setImportError("No se pudo leer el archivo. Asegúrate de que tenga una hoja con datos.");
			setSelectedImportFile(null);
			setParsedImportRows([]);
		} finally {
			setImportParsing(false);
		}
	};

	const openImportFilePicker = () => {
		if (importParsing || importingFile) return;
		fileInputRef.current?.click();
	};

	const buildImportPayload = () => {
		const payload = {
			rows: parsedImportRows,
			origen: "manual",
			triggerLabel: "archivo_importado",
		};

		if (importMode === "ultimos_dias") {
			const since = new Date();
			since.setDate(since.getDate() - (Number(importCantidadDias) - 1));
			payload.desde = toYmd(since);
			payload.hasta = toYmd(new Date());
		} else if (importMode === "fecha_manual") {
			payload.fecha = String(importFecha).trim();
		} else {
			payload.usarFechasExcel = true;
		}

		return payload;
	};

	const handleImportFromFile = async () => {
		setImportError("");
		setSuccess("");
		setImportSuccess("");

		if (!selectedImportFile || parsedImportRows.length === 0) {
			setImportError("Debes seleccionar y parsear un archivo antes de importar.");
			return;
		}

		if (importMode === "fecha_manual" && !String(importFecha).trim()) {
			setImportError("Debes indicar una fecha válida.");
			return;
		}

		if (importMode === "ultimos_dias" && Number(importCantidadDias) <= 0) {
			setImportError("Los días a sincronizar deben ser mayores que 0.");
			return;
		}

		if (importMode === "fecha_excel") {
			const rowsWithoutFecha = parsedImportRows.filter((row) => !row.FECHA);
			if (rowsWithoutFecha.length > 0) {
				setImportError(`Hay ${rowsWithoutFecha.length} fila(s) sin FECHA. Corrige el Excel o usa una fecha manual.`);
				return;
			}
		}

		setImportingFile(true);
		setError("");

		try {
			const response = await sincronizar(buildImportPayload());

			if (response?.success) {
				setImportSuccess("Importación completada correctamente.");
				setImportSuccess(
					response?.summary?.resumen || response?.resumen || "ImportaciÃ³n completada correctamente."
				);
				setParsedImportRows([]);
				setSelectedImportFile(null);
				setImportCantidadDias(1);
				setImportMode("fecha_excel");
				setImportFecha(toYmd(new Date()));
				setSuccess("");
				setImportSuccess(
					response?.summary?.resumen || response?.resumen || "SincronizaciÃ³n lanzada correctamente"
				);
				await cargarDatos();
			} else {
				setImportError(response?.message || "No se pudo importar el archivo.");
			}
		} catch (err) {
			console.error("Error importando archivo de partes:", err);
			setImportError("No se pudo importar el archivo. Revisa el formato y vuelve a intentar.");
		} finally {
			setImportingFile(false);
		}
	};

	const limpiarExcel = () => {
		setSelectedImportFile(null);
		setParsedImportRows([]);
		setImportError("");
		setImportSuccess("");
		setImportCantidadDias(1);
		setImportMode("fecha_excel");
		setImportFecha(toYmd(new Date()));
	};

	const abrirNueva = () => {
		setForm(emptyForm);
		setFormError("");
		setFormOpen(true);
	};

	const abrirEdicion = (config) => {
		setForm({
			id: config.id,
			nombre: config.nombre ?? "",
			descripcion: config.descripcion ?? "",
			activo: Number(config.activo ?? 1),
			frecuencia_tipo: config.frecuencia_tipo ?? "diaria",
			dias_semana: normalizeWeekdays(config.dias_semana),
			hora_ejecucion: formatHora(config.hora_ejecucion || "04:30:00"),
			modo_sync: config.modo_sync ?? "hoy",
			cantidad_dias: Number(config.cantidad_dias ?? 1),
			nif_cabo: config.nif_cabo ?? "",
		});
		setFormError("");
		setFormOpen(true);
	};

	const cerrarForm = () => {
		if (saving) return;

		setFormOpen(false);
		setForm(emptyForm);
		setFormError("");
	};

	const validarForm = () => {
		if (!String(form.nombre ?? "").trim()) {
			return "El nombre es obligatorio";
		}

		if (!String(form.hora_ejecucion ?? "").trim()) {
			return "La hora de ejecución es obligatoria";
		}

		if (form.frecuencia_tipo === "semanal" && normalizeWeekdays(form.dias_semana).length === 0) {
			return "Debes seleccionar al menos un día de la semana";
		}

		if (form.modo_sync === "ultimos_dias" && Number(form.cantidad_dias) <= 0) {
			return "Los días a sincronizar deben ser mayores que 0";
		}

		return "";
	};

	const guardarForm = async () => {
		const validation = validarForm();

		if (validation) {
			setFormError(validation);
			return;
		}

		setSaving(true);
		setFormError("");
		setError("");
		setSuccess("");

		const payload = {
			nombre: String(form.nombre).trim(),
			descripcion: String(form.descripcion ?? "").trim(),
			activo: Number(form.activo) === 1 ? 1 : 0,
			frecuencia_tipo: form.frecuencia_tipo,
			dias_semana: normalizeWeekdays(form.dias_semana),
			hora_ejecucion: String(form.hora_ejecucion).trim(),
			modo_sync: form.modo_sync,
			cantidad_dias: Number(form.cantidad_dias),
			nif_cabo: String(form.nif_cabo ?? "").trim(),
		};

		try {
			const response = form.id
				? await updateSincronizacionEoer(form.id, payload)
				: await createSincronizacionEoer(payload);

			if (response?.success) {
				setSuccess(
					form.id
						? "Configuración actualizada correctamente"
						: "Configuración creada correctamente"
				);
				cerrarForm();
				await cargarDatos();
			} else {
				setFormError(response?.message || "No se pudo guardar la configuración");
			}
		} catch (err) {
			console.error("Error guardando sincronización EOER:", err);
			setFormError("No se pudo guardar la configuración");
		} finally {
			setSaving(false);
		}
	};

	const cambiarEstado = async (config) => {
		setSaving(true);
		setError("");
		setSuccess("");

		try {
			const response = await toggleSincronizacionEoer(config.id);

			if (response?.success) {
				setSuccess(response?.message || "Estado actualizado");
				await cargarDatos();
			} else {
				setError(response?.message || "No se pudo cambiar el estado");
			}
		} catch (err) {
			console.error("Error cambiando estado de sincronización EOER:", err);
			setError("No se pudo cambiar el estado");
		} finally {
			setSaving(false);
		}
	};

	const eliminar = async (config) => {
		const confirmDelete = window.confirm(
			`¿Seguro que quieres eliminar la configuración "${config.nombre}"?`
		);

		if (!confirmDelete) return;

		setSaving(true);
		setError("");
		setSuccess("");

		try {
			const response = await deleteSincronizacionEoer(config.id);

			if (response?.success) {
				setSuccess(response?.message || "Configuración eliminada");
				await cargarDatos();
			} else {
				setError(response?.message || "No se pudo eliminar la configuración");
			}
		} catch (err) {
			console.error("Error eliminando sincronización EOER:", err);
			setError("No se pudo eliminar la configuración");
		} finally {
			setSaving(false);
		}
	};

	const ejecutarAhora = async (config) => {
		setRunningId(config.id);
		setError("");
		setSuccess("");

		try {
			const payload = buildSyncPayloadFromConfig(config);
			const response = await sincronizar({
				...payload,
				origen: "manual",
				idConfiguracion: config.id,
				triggerLabel: "panel_admin",
			});

			if (response?.success) {
				setSuccess(
					response?.summary?.resumen || response?.resumen || "Importación completada correctamente."
				);
				await cargarDatos();
			} else {
				setError(response?.message || "No se pudo ejecutar la sincronización");
			}
		} catch (err) {
			console.error("Error ejecutando sincronización EOER:", err);
			setError("No se pudo ejecutar la sincronización");
		} finally {
			setRunningId(null);
		}
	};

	return (
		<Grid container direction="column" size={{ xs: 12 }} sx={{ px: 2, pb: 6 }}>
			<SubHeader titulo="Sincronizaciones EOER" />

			<Grid container spacing={2} sx={{ mb: 2 }}>
				{estadisticas.map((item) => (
					<Grid key={item.label} size={{ xs: 12, sm: 6, lg: 3 }}>
						<Card elevation={2}>
							<CardContent>
								<Typography variant="overline" color="text.secondary">
									{item.label}
								</Typography>
								<Typography variant="h5" sx={{ mt: 1 }}>
									{item.value}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{item.helper}
								</Typography>
							</CardContent>
						</Card>
					</Grid>
				))}
			</Grid>

			<Alert severity="info" sx={{ mb: 2 }}>
				Las sincronizaciones automáticas se programan por día y hora exacta. El
				servidor debe llamar periódicamente a `ejecutarSincronizacionesEoer.php`
				para ejecutar las que estén pendientes.
			</Alert>

			{!schemaReady && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					Faltan tablas para guardar configuraciones e historial.
					{missingTables.length > 0
						? ` Pendientes: ${missingTables.join(", ")}.`
						: ""}
				</Alert>
			)}

			{error && (
				<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
					{error}
				</Alert>
			)}

			{success && (
				<Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
					{success}
				</Alert>
			)}

			<Paper elevation={2} sx={{ p: 2, mb: 3 }}>
				<Grid container spacing={2} alignItems="center">
					<Grid size={{ xs: 12, md: 6 }}>
						<Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<ScheduleIcon color="primary" />
							Configuraciones automáticas
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
							Configura reglas como “todos los días a las 04:30” o “lunes y
							miércoles a las 04:30”, y decide si sincronizan solo hoy o un
							pequeño rango reciente.
						</Typography>
					</Grid>

					<Grid size={{ xs: 12, md: 6 }}>
						<Box sx={{ display: "flex", gap: 1, justifyContent: { xs: "flex-start", md: "flex-end" } }}>
							<Button
								variant="outlined"
								startIcon={<RefreshIcon />}
								onClick={cargarDatos}
								disabled={loading}
							>
								Recargar
							</Button>
							<Button
								variant="contained"
								startIcon={<AddIcon />}
								onClick={abrirNueva}
								disabled={!schemaReady}
							>
								Nueva sincronización
							</Button>
						</Box>
					</Grid>
				</Grid>
			</Paper>

			{loading ? (
				<Box
					sx={{
						minHeight: 240,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<CircularProgress />
				</Box>
			) : (
				<Grid container spacing={2} sx={{ mb: 4 }}>
					{configuraciones.length === 0 ? (
						<Grid size={{ xs: 12 }}>
							<Paper elevation={1} sx={{ p: 3 }}>
								<Typography variant="body1">
									No hay sincronizaciones configuradas todavía.
								</Typography>
							</Paper>
						</Grid>
					) : (
						configuraciones.map((config) => (
							<Grid key={config.id} size={{ xs: 12, lg: 6 }}>
								<Card elevation={2} sx={{ height: "100%" }}>
									<CardContent>
										<Box
											sx={{
												display: "flex",
												justifyContent: "space-between",
												gap: 2,
												flexWrap: "wrap",
												mb: 1.5,
											}}
										>
											<Box>
												<Typography variant="h6">{config.nombre}</Typography>
												<Typography variant="body2" color="text.secondary">
													{config.descripcion || "Sin descripción"}
												</Typography>
											</Box>

											<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
												<Chip
													size="small"
													color={config.activo === 1 ? "success" : "default"}
													label={config.activo === 1 ? "Activa" : "Pausada"}
												/>
												{config.ultima_ejecucion_estado && (
													<Chip
														size="small"
														color={formatEstadoColor(config.ultima_ejecucion_estado)}
														label={formatEstadoLabel(config.ultima_ejecucion_estado)}
													/>
												)}
											</Box>
										</Box>

										<Grid container spacing={1.5} sx={{ mb: 2 }}>
											<Grid size={{ xs: 12, sm: 6 }}>
												<Typography variant="caption" color="text.secondary">
													Programación
												</Typography>
												<Typography variant="body2">{formatProgramacion(config)}</Typography>
											</Grid>
											<Grid size={{ xs: 12, sm: 6 }}>
												<Typography variant="caption" color="text.secondary">
													Periodo a sincronizar
												</Typography>
												<Typography variant="body2">{formatModo(config)}</Typography>
											</Grid>
											<Grid size={{ xs: 12, sm: 6 }}>
												<Typography variant="caption" color="text.secondary">
													Próxima ejecución
												</Typography>
												<Typography variant="body2">
													{formatDateTimeEs(config.proxima_ejecucion)}
												</Typography>
											</Grid>
											<Grid size={{ xs: 12, sm: 6 }}>
												<Typography variant="caption" color="text.secondary">
													Última ejecución
												</Typography>
												<Typography variant="body2">
													{formatDateTimeEs(config.ultima_ejecucion)}
												</Typography>
											</Grid>
											<Grid size={{ xs: 12 }}>
												<Typography variant="caption" color="text.secondary">
													Filtro de cabo
												</Typography>
												<Typography variant="body2">
													{config.nif_cabo || "Todos"}
												</Typography>
											</Grid>
											{config.ultima_ejecucion_resumen && (
												<Grid size={{ xs: 12 }}>
													<Typography variant="caption" color="text.secondary">
														Último resultado
													</Typography>
													<Typography variant="body2">
														{config.ultima_ejecucion_resumen}
													</Typography>
												</Grid>
											)}
										</Grid>

										<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
											<Tooltip title="Ejecutar ahora">
												<span>
													<Button
														variant="contained"
														size="small"
														startIcon={<PlayArrowIcon />}
														onClick={() => ejecutarAhora(config)}
														disabled={runningId === config.id}
													>
														{runningId === config.id ? "Ejecutando..." : "Ejecutar"}
													</Button>
												</span>
											</Tooltip>

											<Button
												variant="outlined"
												size="small"
												startIcon={<EditIcon />}
												onClick={() => abrirEdicion(config)}
											>
												Editar
											</Button>

											<Button
												variant="outlined"
												size="small"
												color={config.activo === 1 ? "warning" : "success"}
												startIcon={<SettingsBackupRestoreIcon />}
												onClick={() => cambiarEstado(config)}
											>
												{config.activo === 1 ? "Pausar" : "Activar"}
											</Button>

											<IconButton color="error" onClick={() => eliminar(config)}>
												<DeleteIcon />
											</IconButton>
										</Box>
									</CardContent>
								</Card>
							</Grid>
						))
					)}
				</Grid>
			)}

			<Paper elevation={2} sx={{ p: 2, mb: 3 }}>
				<Grid container spacing={2} alignItems="center">
					<Grid size={{ xs: 12, md: 7 }}>
						<Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<CloudUploadOutlinedIcon color="primary" />
							Subir Excel
						</Typography>

						<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
							Sube una plantilla Excel con las columnas FECHA, REF CLIENTE,
							NOMBRE CLIENTE, NOMBRE CABO y NIF CABO para lanzar una
							sincronización manual desde archivo.
						</Typography>
					</Grid>

					<Grid size={{ xs: 12, md: 5 }}>
						<Box
							sx={{
								display: "flex",
								gap: 1,
								justifyContent: { xs: "flex-start", md: "flex-end" },
								flexWrap: "wrap",
							}}
						>
							<input
								ref={fileInputRef}
								type="file"
								accept=".xlsx,.xls,.csv"
								style={{ display: "none" }}
								onChange={(event) => {
									handleImportFileSelection(event.target.files);
									event.target.value = "";
								}}
							/>

							<Button
								variant="outlined"
								startIcon={<CloudUploadOutlinedIcon />}
								onClick={openImportFilePicker}
								disabled={importParsing || importingFile}
							>
								Seleccionar Excel
							</Button>

							<Button
								variant="contained"
								startIcon={<PlayArrowIcon />}
								onClick={handleImportFromFile}
								disabled={
									importParsing ||
									importingFile ||
									!selectedImportFile ||
									parsedImportRows.length === 0
								}
							>
								{importingFile ? "Importando..." : "Sincronizar Excel"}
							</Button>

							<Button
								variant="text"
								color="inherit"
								onClick={limpiarExcel}
								disabled={importParsing || importingFile}
							>
								Limpiar
							</Button>
						</Box>
					</Grid>

					<Grid size={{ xs: 12 }}>
						<Grid container spacing={2} alignItems="center">
							<Grid size={{ xs: 12, sm: 6, md: 3 }}>
								<FormControl fullWidth size="small">
									<InputLabel id="import-mode-label">Fecha de sincronización</InputLabel>
									<Select
										labelId="import-mode-label"
										label="Fecha de sincronización"
										value={importMode}
										onChange={(event) => setImportMode(event.target.value)}
									>
										<MenuItem value="fecha_excel">Usar FECHA del Excel</MenuItem>
										<MenuItem value="fecha_manual">Usar una fecha manual</MenuItem>
										<MenuItem value="ultimos_dias">Últimos N días</MenuItem>
									</Select>
								</FormControl>
							</Grid>

							{importMode === "fecha_manual" && (
								<Grid size={{ xs: 12, sm: 6, md: 3 }}>
									<TextField
										fullWidth
										size="small"
										type="date"
										label="Fecha"
										value={importFecha}
										onChange={(event) => setImportFecha(event.target.value)}
										InputLabelProps={{ shrink: true }}
									/>
								</Grid>
							)}

							{importMode === "ultimos_dias" && (
								<Grid size={{ xs: 12, sm: 6, md: 3 }}>
									<TextField
										fullWidth
										size="small"
										type="number"
										label="Días"
										value={importCantidadDias}
										onChange={(event) => setImportCantidadDias(Number(event.target.value))}
										helperText="1 significa solo hoy."
									/>
								</Grid>
							)}

							<Grid size={{ xs: 12, md: 6 }}>
								{selectedImportFile ? (
									<Alert severity="info">
										Archivo seleccionado: <strong>{selectedImportFile.name}</strong>
										{" — "}
										{parsedImportRows.length} fila(s) detectada(s).
									</Alert>
								) : (
									<Alert severity="info">
										No hay ningún archivo seleccionado.
									</Alert>
								)}
							</Grid>
						</Grid>
					</Grid>

					{importParsing && (
						<Grid size={{ xs: 12 }}>
							<Alert severity="info">Leyendo archivo...</Alert>
						</Grid>
					)}

					{importError && (
						<Grid size={{ xs: 12 }}>
							<Alert severity="error" onClose={() => setImportError("")}>
								{importError}
							</Alert>
						</Grid>
					)}

					{importSuccess && (
						<Grid size={{ xs: 12 }}>
							<Alert severity="success" onClose={() => setImportSuccess("")}>
								{importSuccess}
							</Alert>
						</Grid>
					)}

					{parsedImportRows.length > 0 && (
						<Grid size={{ xs: 12 }}>
							<Typography variant="subtitle2" sx={{ mb: 1 }}>
								Previsualización
							</Typography>

							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>FECHA</TableCell>
										<TableCell>REF CLIENTE</TableCell>
										<TableCell>NOMBRE CLIENTE</TableCell>
										<TableCell>NOMBRE CABO</TableCell>
										<TableCell>NIF CABO</TableCell>
									</TableRow>
								</TableHead>

								<TableBody>
									{parsedImportRows.slice(0, 10).map((row, index) => (
										<TableRow key={`${row["NIF CABO"] || "fila"}-${index}`}>
											<TableCell>{row.FECHA || "-"}</TableCell>
											<TableCell>{row["REF CLIENTE"] || "-"}</TableCell>
											<TableCell>{row["NOMBRE CLIENTE"] || "-"}</TableCell>
											<TableCell>{row["NOMBRE CABO"] || "-"}</TableCell>
											<TableCell>{row["NIF CABO"] || "-"}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>

							{parsedImportRows.length > 10 && (
								<Typography variant="caption" color="text.secondary">
									Mostrando las primeras 10 filas de {parsedImportRows.length}.
								</Typography>
							)}
						</Grid>
					)}
				</Grid>
			</Paper>

			<Paper elevation={2} sx={{ p: 2 }}>
				<Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
					<Grid size={{ xs: 12, md: 4 }}>
						<Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<SyncAltIcon color="primary" />
							Historial de ejecuciones
						</Typography>
					</Grid>
					<Grid size={{ xs: 12, sm: 6, md: 3 }}>
						<FormControl fullWidth size="small">
							<InputLabel id="historial-origen-label">Origen</InputLabel>
							<Select
								labelId="historial-origen-label"
								label="Origen"
								value={historialOrigen}
								onChange={(event) => setHistorialOrigen(event.target.value)}
							>
								<MenuItem value="todos">Todos</MenuItem>
								<MenuItem value="manual">Manual</MenuItem>
								<MenuItem value="automatica">Automática</MenuItem>
							</Select>
						</FormControl>
					</Grid>
					<Grid size={{ xs: 12, sm: 6, md: 3 }}>
						<FormControl fullWidth size="small">
							<InputLabel id="historial-estado-label">Estado</InputLabel>
							<Select
								labelId="historial-estado-label"
								label="Estado"
								value={historialEstado}
								onChange={(event) => setHistorialEstado(event.target.value)}
							>
								<MenuItem value="todos">Todos</MenuItem>
								<MenuItem value="completada">Completada</MenuItem>
								<MenuItem value="parcial">Parcial</MenuItem>
								<MenuItem value="error">Error</MenuItem>
								<MenuItem value="ejecutando">Ejecutando</MenuItem>
							</Select>
						</FormControl>
					</Grid>
					<Grid size={{ xs: 12, md: 2 }}>
						<Button
							fullWidth
							variant="outlined"
							startIcon={<RefreshIcon />}
							onClick={cargarDatos}
						>
							Actualizar
						</Button>
					</Grid>
				</Grid>

				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Inicio</TableCell>
							<TableCell>Configuración</TableCell>
							<TableCell>Origen</TableCell>
							<TableCell>Estado</TableCell>
							<TableCell>Periodo</TableCell>
							<TableCell>Resumen</TableCell>
							<TableCell align="right">Detalle</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{historial.length === 0 ? (
							<TableRow>
								<TableCell colSpan={7}>
									<Typography variant="body2">
										No hay ejecuciones registradas.
									</Typography>
								</TableCell>
							</TableRow>
						) : (
							historial.map((item) => (
								<TableRow key={item.id} hover>
									<TableCell>{formatDateTimeEs(item.fecha_inicio)}</TableCell>
									<TableCell>{item.configuracion_nombre || "Manual sin regla"}</TableCell>
									<TableCell>
										<Chip
											size="small"
											variant="outlined"
											label={formatOrigenLabel(item.origen)}
										/>
									</TableCell>
									<TableCell>
										<Chip
											size="small"
											color={formatEstadoColor(item.estado)}
											label={formatEstadoLabel(item.estado)}
										/>
									</TableCell>
									<TableCell>
										{item.modo === "rango"
											? `${formatDateTimeEs(item.fecha_desde, { includeTime: false })} - ${formatDateTimeEs(
													item.fecha_hasta,
													{ includeTime: false }
											  )}`
											: formatDateTimeEs(item.fecha_dia, { includeTime: false })}
									</TableCell>
									<TableCell>{item.resumen || item.error_texto || "-"}</TableCell>
									<TableCell align="right">
										<Button size="small" onClick={() => setDetailItem(item)}>
											Ver detalle
										</Button>
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</Paper>

			<Dialog open={formOpen} onClose={cerrarForm} fullWidth maxWidth="sm">
				<DialogTitle>
					{form.id ? "Editar sincronización EOER" : "Nueva sincronización EOER"}
				</DialogTitle>
				<DialogContent>
					<Grid container spacing={2} sx={{ mt: 0.5 }}>
						<Grid size={{ xs: 12 }}>
							<TextField
								fullWidth
								size="small"
								label="Nombre"
								value={form.nombre}
								onChange={(event) => setForm((current) => ({ ...current, nombre: event.target.value }))}
							/>
						</Grid>
						<Grid size={{ xs: 12 }}>
							<TextField
								fullWidth
								size="small"
								label="Descripción"
								value={form.descripcion}
								onChange={(event) =>
									setForm((current) => ({ ...current, descripcion: event.target.value }))
								}
							/>
						</Grid>
						<Grid size={{ xs: 12, sm: 4 }}>
							<FormControl fullWidth size="small">
								<InputLabel id="frecuencia-tipo-label">Frecuencia</InputLabel>
								<Select
									labelId="frecuencia-tipo-label"
									label="Frecuencia"
									value={form.frecuencia_tipo}
									onChange={(event) =>
										setForm((current) => ({
											...current,
											frecuencia_tipo: event.target.value,
											dias_semana:
												event.target.value === "diaria" ? [] : current.dias_semana,
										}))
									}
								>
									<MenuItem value="diaria">Todos los días</MenuItem>
									<MenuItem value="semanal">Días concretos</MenuItem>
								</Select>
							</FormControl>
						</Grid>
						<Grid size={{ xs: 12, sm: 4 }}>
							<TextField
								fullWidth
								size="small"
								type="time"
								label="Hora"
								value={form.hora_ejecucion}
								onChange={(event) =>
									setForm((current) => ({
										...current,
										hora_ejecucion: event.target.value,
									}))
								}
								InputLabelProps={{ shrink: true }}
							/>
						</Grid>
						<Grid size={{ xs: 12, sm: 4 }}>
							<FormControl fullWidth size="small">
								<InputLabel id="activo-sincro-label">Estado</InputLabel>
								<Select
									labelId="activo-sincro-label"
									label="Estado"
									value={form.activo}
									onChange={(event) =>
										setForm((current) => ({
											...current,
											activo: Number(event.target.value),
										}))
									}
								>
									<MenuItem value={1}>Activa</MenuItem>
									<MenuItem value={0}>Pausada</MenuItem>
								</Select>
							</FormControl>
						</Grid>
						{form.frecuencia_tipo === "semanal" && (
							<Grid size={{ xs: 12 }}>
								<FormControl fullWidth size="small">
									<InputLabel id="dias-semana-label">Días de la semana</InputLabel>
									<Select
										multiple
										labelId="dias-semana-label"
										label="Días de la semana"
										value={form.dias_semana}
										renderValue={(selected) => formatDiasSemana(selected)}
										onChange={(event) =>
											setForm((current) => ({
												...current,
												dias_semana: normalizeWeekdays(event.target.value),
											}))
										}
									>
										{weekdayOptions.map((option) => (
											<MenuItem key={option.value} value={option.value}>
												{option.label}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
						)}
						<Grid size={{ xs: 12, sm: 6 }}>
							<FormControl fullWidth size="small">
								<InputLabel id="modo-sync-label">Periodo a sincronizar</InputLabel>
								<Select
									labelId="modo-sync-label"
									label="Periodo a sincronizar"
									value={form.modo_sync}
									onChange={(event) =>
										setForm((current) => ({
											...current,
											modo_sync: event.target.value,
										}))
									}
								>
									<MenuItem value="hoy">Solo hoy</MenuItem>
									<MenuItem value="ultimos_dias">Últimos N días</MenuItem>
								</Select>
							</FormControl>
						</Grid>
						<Grid size={{ xs: 12, sm: 6 }}>
							<TextField
								fullWidth
								size="small"
								type="number"
								label="Días a sincronizar"
								value={form.cantidad_dias}
								onChange={(event) =>
									setForm((current) => ({
										...current,
										cantidad_dias: Number(event.target.value),
									}))
								}
								helperText="Si eliges últimos N días, 1 significa solo hoy."
							/>
						</Grid>
						<Grid size={{ xs: 12 }}>
							<TextField
								fullWidth
								size="small"
								label="NIF del cabo (opcional)"
								value={form.nif_cabo}
								onChange={(event) =>
									setForm((current) => ({ ...current, nif_cabo: event.target.value }))
								}
								helperText="Déjalo vacío para sincronizar todos los cabos."
							/>
						</Grid>
					</Grid>

					{formError && (
						<Alert severity="error" sx={{ mt: 2 }}>
							{formError}
						</Alert>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={cerrarForm} disabled={saving}>
						Cancelar
					</Button>
					<Button variant="contained" onClick={guardarForm} disabled={saving}>
						{saving ? "Guardando..." : "Guardar"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={Boolean(detailItem)}
				onClose={() => setDetailItem(null)}
				fullWidth
				maxWidth="md"
			>
				<DialogTitle>Detalle de ejecución</DialogTitle>
				<DialogContent dividers>
					{detailItem && (
						<Grid container spacing={2}>
							<Grid size={{ xs: 12 }}>
								<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
									<Chip
										size="small"
										variant="outlined"
										label={formatOrigenLabel(detailItem.origen)}
									/>
									<Chip
										size="small"
										color={formatEstadoColor(detailItem.estado)}
										label={formatEstadoLabel(detailItem.estado)}
									/>
								</Box>
								<Typography variant="body2">
									<strong>Configuración:</strong>{" "}
									{detailItem.configuracion_nombre || "Manual sin regla"}
								</Typography>
								<Typography variant="body2">
									<strong>Inicio:</strong> {formatDateTimeEs(detailItem.fecha_inicio)}
								</Typography>
								<Typography variant="body2">
									<strong>Fin:</strong> {formatDateTimeEs(detailItem.fecha_fin)}
								</Typography>
								<Typography variant="body2">
									<strong>Resumen:</strong> {detailItem.resumen || "-"}
								</Typography>
								{detailItem.error_texto && (
									<Alert severity="error" sx={{ mt: 2 }}>
										{detailItem.error_texto}
									</Alert>
								)}
							</Grid>

							<Grid size={{ xs: 12, md: 6 }}>
								<Typography variant="subtitle2" sx={{ mb: 1 }}>
									Payload
								</Typography>
								<Box
									component="pre"
									sx={{
										p: 2,
										bgcolor: "grey.100",
										borderRadius: 1,
										overflowX: "auto",
										fontSize: 12,
									}}
								>
									{prettyJson(detailItem.payload_json) || "{}"}
								</Box>
							</Grid>

							<Grid size={{ xs: 12, md: 6 }}>
								<Typography variant="subtitle2" sx={{ mb: 1 }}>
									Resultado
								</Typography>
								<Box
									component="pre"
									sx={{
										p: 2,
										bgcolor: "grey.100",
										borderRadius: 1,
										overflowX: "auto",
										fontSize: 12,
									}}
								>
									{prettyJson(detailItem.resultado_json) || "{}"}
								</Box>
							</Grid>
						</Grid>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDetailItem(null)}>Cerrar</Button>
				</DialogActions>
			</Dialog>
		</Grid>
	);
};
